import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import IconSelector from "components/shared/images/IconSelector";

import { IContainerMailDomainData } from "types/api/containerMailDomainApiInterface";

import EditFail2ban from "../actions/fail2banActions/EditFail2ban";

const MailDomainFail2ban = ({ mailData }: { mailData: IContainerMailDomainData }): ReactElement => {
 const intl = useIntl();

 const [showUpdate, setShowUpdate] = useState<boolean>(false);

 const openUpdate = () => setShowUpdate(true);
 const closeUpdate = () => setShowUpdate(false);

 return (
  <>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center">
     <IconSelector icon="NotInterestedIcon" props={{ color: "kxOrange" }} />
     <Typography fontSize={16} ml={1} variant="unboundedTitle">
      <FormattedMessage id="container.maildomains.fail2ban" />
     </Typography>
    </Stack>
    <Stack mt={2}>
     {showUpdate ? (
      <EditFail2ban mailData={mailData} closeUpdate={closeUpdate} />
     ) : (
      <Stack textAlign="left">
       <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontWeight="bold" variant="unboundedTitle" fontSize={14}>
         Ban time
        </Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.banTime" />
        </Typography>
        <Typography variant="subtitle2">{mailData.fail2ban.ban_time}</Typography>
       </Stack>
       <Stack direction="row" alignItems="center">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.banTimeIncrement" />
        </Typography>
        <Chip
         label={mailData.fail2ban.ban_time_increment ? "Online" : "Offline"}
         size="small"
         color={mailData.fail2ban.ban_time_increment ? "success" : "error"}
         sx={{ height: "18px" }}
        />
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.maxBanTime" />
        </Typography>
        <Typography variant="subtitle2">{mailData.fail2ban.max_ban_time}</Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.maxAttempts" />
        </Typography>
        <Typography variant="subtitle2">{mailData.fail2ban.max_attempts}</Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.retryWindow" />
        </Typography>
        <Typography variant="subtitle2">{mailData.fail2ban.retry_window}</Typography>
       </Stack>
       <Divider sx={{ my: 1 }} />
       <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontWeight="bold" variant="unboundedTitle" fontSize={14}>
         Net Ban
        </Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.netbanIpv4" />
        </Typography>
        <Typography variant="subtitle2">{mailData.fail2ban.netban_ipv4}</Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.netbanIpv6" />
        </Typography>
        <Typography variant="subtitle2">{mailData.fail2ban.netban_ipv6}</Typography>
       </Stack>
       <Divider sx={{ my: 1 }} />
       <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontWeight="bold" variant="unboundedTitle" fontSize={14}>
         Lists
        </Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.whitelist" />
        </Typography>
        <Typography variant="subtitle2">
         {mailData.fail2ban.whitelist.length > 0
          ? mailData.fail2ban.whitelist.map((element, index) =>
             index !== mailData.fail2ban.whitelist.length - 1 ? `${element}, ` : element
            )
          : "No element"}
        </Typography>
       </Stack>
       <Stack direction="row">
        <Typography fontWeight="bold" variant="subtitle2" minWidth={180}>
         <FormattedMessage id="container.maildomains.blacklist" />
        </Typography>
        <Typography variant="subtitle2">
         {mailData.fail2ban.blacklist.length > 0
          ? mailData.fail2ban.blacklist.map((element) => `${element} `)
          : "No element"}
        </Typography>
       </Stack>
       <Divider sx={{ my: 1 }} />
       <Stack direction="row" alignItems="center" spacing={1}>
        <Typography fontWeight="bold" variant="unboundedTitle" fontSize={14}>
         Actions
        </Typography>
       </Stack>
       <Stack direction="row">
        <Tooltip title={intl.formatMessage({ id: "app.edit" })} placement="top">
         <IconButton onClick={openUpdate}>
          <IconSelector icon="EditIcon" props={{ sx: { fontSize: 16 }, color: "kxOrange" }} />
         </IconButton>
        </Tooltip>
       </Stack>
      </Stack>
     )}
    </Stack>
   </Paper>
   <Paper elevation={1} sx={{ borderRadius: "5px", p: 2, mt: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center">
     <IconSelector icon="NotInterestedIcon" props={{ color: "kxOrange" }} />
     <Typography fontSize={16} ml={1} variant="unboundedTitle">
      Active Ban
     </Typography>
    </Stack>
    <Stack mt={2}>
     {mailData.fail2ban.active_bans.length > 0 ? (
      <TableContainer component="div">
       <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
         <TableRow>
          <TableCell>
           <FormattedMessage id="container.maildomains.queueUnban" />
          </TableCell>
          <TableCell>
           <FormattedMessage id="container.maildomains.network" />
          </TableCell>
          <TableCell>
           <FormattedMessage id="container.maildomains.ip" />
          </TableCell>
          <TableCell>
           <FormattedMessage id="container.maildomains.bannedUntil" />
          </TableCell>
         </TableRow>
        </TableHead>
        <TableBody>
         {mailData.fail2ban.active_bans?.map((banned, index) => {
          return (
           <TableRow key={`ban-list-${index}`}>
            <TableCell>
             <Stack direction="row" spacing={1} alignItems="center">
              <IconSelector
               icon={banned.queued_for_unban === 0 ? "CancelIcon" : "CheckCircleIcon"}
               props={{
                color: banned.queued_for_unban === 0 ? "error" : "success",
                sx: { fontSize: 20 }
               }}
              />
              <Typography variant="caption">
               <FormattedMessage
                id={
                 banned.queued_for_unban === 0
                  ? "container.maildomains.notInQueue"
                  : "container.maildomains.inQueue"
                }
               />
              </Typography>
             </Stack>
            </TableCell>
            <TableCell>
             <Typography variant="caption">{banned.network}</Typography>
            </TableCell>
            <TableCell>
             <Typography variant="caption">{banned.ip}</Typography>
            </TableCell>
            <TableCell>
             <Typography variant="caption">{banned.banned_until}</Typography>
            </TableCell>
           </TableRow>
          );
         })}
        </TableBody>
       </Table>
      </TableContainer>
     ) : (
      <Typography fontStyle="italic" textAlign="left" variant="subtitle2">
       <FormattedMessage id="container.maildomains.noActiveBans" />
      </Typography>
     )}
    </Stack>
   </Paper>
  </>
 );
};

export default MailDomainFail2ban;
